import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Path from '../../Path';
import { useLanguage } from '../../i18n/useLanguage';
import { cn } from '../utils/cn';

import type { FooterSection, LayoutVariant, NavItem, SocialLink } from './layoutTypes';

interface FooterProps {
    variant: LayoutVariant;
    /** Colonnes de liens (variante standard). */
    sections?: FooterSection[];
    socialLinks?: SocialLink[];
    /** Mentions légales, confidentialité, conditions... (variante standard). */
    legalLinks?: NavItem[];
}

const FOCUS =
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 dark:focus-visible:outline-white';

/**
 * Footer réutilisé par les deux variantes du layout.
 * - map : seule la marque, en bas de la carte, là où Google Maps affiche ses mentions ;
 * - standard : pied de page classique (liens, mentions légales, réseaux), comme Airbnb.
 */
export default function Footer({
    variant,
    sections = [],
    socialLinks = [],
    legalLinks = [],
}: FooterProps) {
    const { t } = useTranslation();
    const { localize } = useLanguage();
    const brand = t('layout.footer.brand');

    if (variant === 'map') {
        return (
            <footer className="flex justify-center px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
                <Link
                    to={localize(Path.MAP)}
                    className={cn(
                        'pointer-events-auto rounded bg-white/80 px-2 py-0.5 text-xs font-medium text-gray-700 backdrop-blur dark:bg-gray-900/80 dark:text-gray-200',
                        FOCUS,
                    )}
                >
                    {brand}
                </Link>
            </footer>
        );
    }

    return (
        <footer className="border-t border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900">
            <div className="mx-auto max-w-7xl px-4 py-8 pb-[max(2rem,env(safe-area-inset-bottom))] sm:px-6 md:py-10 lg:px-8">
                <div className="grid gap-8 lg:grid-cols-4">
                    <div>
                        <p className="text-lg font-semibold">{brand}</p>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            {t('app.tagline')}
                        </p>
                    </div>
                    {sections.length > 0 && (
                        <nav
                            aria-label={t('layout.footer.nav')}
                            className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-3"
                        >
                            {sections.map((section) => (
                                <div key={section.title}>
                                    <h2 className="text-sm font-semibold">{section.title}</h2>
                                    <ul className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                        {section.items.map((item) => (
                                            <li key={item.to}>
                                                <Link
                                                    to={item.to}
                                                    className={cn(
                                                        'inline-block rounded py-1 hover:underline',
                                                        FOCUS,
                                                    )}
                                                >
                                                    {item.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </nav>
                    )}
                </div>

                <div className="mt-8 flex flex-col gap-4 border-t border-gray-200 pt-6 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between dark:border-gray-800 dark:text-gray-400">
                    <p>{t('layout.footer.copyright', { year: new Date().getFullYear() })}</p>
                    {legalLinks.length > 0 && (
                        <nav aria-label={t('layout.footer.legal')}>
                            <ul className="flex flex-wrap gap-x-4 gap-y-1">
                                {legalLinks.map((item) => (
                                    <li key={item.to}>
                                        <Link
                                            to={item.to}
                                            className={cn('rounded hover:underline', FOCUS)}
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}
                    {socialLinks.length > 0 && (
                        <ul aria-label={t('layout.footer.social')} className="flex gap-3">
                            {socialLinks.map(({ label, href, icon: Icon }) => (
                                <li key={href}>
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={label}
                                        className={cn(
                                            'inline-flex rounded p-1 hover:text-gray-900 dark:hover:text-white',
                                            FOCUS,
                                        )}
                                    >
                                        <Icon aria-hidden="true" className="size-5" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </footer>
    );
}
